<template>
    <ValidationObserver v-slot="{ invalid }">
        <v-card :class="{ jsCardHasError: invalid }">
            <v-card-text>
                <form
                    action=""
                    method="POST"
                    @submit.prevent="save"
                >
                    <!--
                    Font Configurator
                    -->
                    <div class="FontConfigurator">
                        <v-row
                            class="FontConfigurator__inputs"
                            dense
                        >
                            <v-col
                                cols="12"
                                sm="8"
                            >
                                <ValidationProvider
                                    name="name"
                                    rules="required|max:255"
                                    v-slot="{ errors }"
                                >
                                    <v-text-field
                                        label="Name"
                                        name="name"
                                        id="name"
                                        v-model="name"
                                        outlined
                                        :error-messages="errors[0]"
                                    />
                                </ValidationProvider>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="4"
                            >
                                <ValidationProvider
                                    name="order"
                                    rules="integer"
                                    v-slot="{ errors }"
                                >
                                    <v-text-field
                                        label="Order"
                                        name="order"
                                        id="order"
                                        v-model="order"
                                        outlined
                                        :error-messages="errors[0]"
                                        type="number"
                                    />
                                </ValidationProvider>
                            </v-col>

                            <v-expansion-panels
                                multiple
                                inset
                                v-if="pairings"
                            >
                                <v-expansion-panel
                                    v-for="(font, id) in pairings"
                                    :key="id"
                                >
                                    <v-expansion-panel-header>{{ font.name }}</v-expansion-panel-header>
                                    <v-expansion-panel-content eager>
                                        <SingleFont
                                            v-if="fontsLoaded"
                                            :key="id"
                                            :id="id"
                                            :fonts="fonts"
                                            :font="font"
                                        />
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                    <v-expansion-panel-header disable-icon-rotate>
                                        Google Font Cache
                                        <template #actions>
                                            <v-icon
                                                small
                                                color="warning"
                                            >
                                                fas fa-exclamation-triangle
                                            </v-icon>
                                        </template>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <p>
                                            Google fonts are cached to avoid hitting their API every time we need a list
                                            of fonts. If Google has added new fonts, you'll need to update the cache to make
                                            them available.
                                        </p>
                                        <v-alert
                                            type="warning"
                                            dense
                                            elevation="1"
                                        >
                                            There is a not-insignificant chance that this will break font selection
                                            throughout the application if the Google API has changed. You should probably test this on staging first.
                                        </v-alert>
                                        <v-card-actions>
                                            <v-checkbox
                                                label="I promise that I tested."
                                                v-model="cacheFontButtonEnabled"
                                                color="warning"
                                            />
                                            <v-spacer />
                                            <v-btn
                                                type="button"
                                                color="warning"
                                                @click="updateFontCache"
                                                :disabled="!cacheFontButtonEnabled"
                                            >
                                                <v-icon
                                                    left
                                                    small
                                                >
                                                    fas fa-download
                                                </v-icon>
                                                Update Google Fonts
                                            </v-btn>
                                        </v-card-actions>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-row>

                        <div class="FontConfigurator__preview">
                            <div
                                class="FontConfigurator__preview__h1"
                                :style="h1Style"
                            >
                                Family Restaurant Rescue
                            </div>
                            <div
                                class="FontConfigurator__preview__h2"
                                :style="h2Style"
                            >
                                I'm Here to Help
                            </div>
                            <div
                                class="FontConfigurator__preview__subhead"
                                :style="subheadStyle"
                            >
                                Small Business Coaching for Family Owned Restaurants
                            </div>
                            <div
                                class="FontConfigurator__preview__banner"
                                :style="bannerStyle"
                            >
                                More Profits
                            </div>
                            <div class="FontConfigurator__preview__body">
                                <p :style="bodyStyle">
                                    Running a successful restaurant is one of the hardest things anyone can attempt to do. Throw
                                    in family dynamics and it can feel nearly impossible. Believe me, I get it. I've seen the
                                    struggle up close with my clients, hard-working people who try their best but always seem
                                    to come up short. It breaks my heart because I know the struggle and ultimate failue is all
                                    avoidable.
                                </p>
                            </div>
                            <div
                                class="FontConfigurator__preview__button"
                                :style="buttonStyle"
                            >
                                <span>Free Restaurant Marketing Audit</span>
                            </div>

                            <div class="FontConfigurator__preview__swatch">
                                <div
                                    class="FontConfigurator__preview__swatch__h1"
                                    :style="h1SwatchStyle"
                                >
                                    Family Restaurant Rescue
                                </div>
                                <div class="FontConfigurator__preview__swatch__body">
                                    <p :style="bodySwatchStyle">
                                        Running a successful restaurant is one of the hardest things anyone can attempt to do. Throw
                                        in family dynamics and it can feel nearly impossible.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    v-if="fontPairingId"
                    text
                    @click="toggleDeleteModal(true)"
                    :disabled="invalid"
                >
                    Delete Font Pairing
                </v-btn>
                <v-spacer />
                <v-switch
                    v-model="published"
                    :label="publishedText"
                    class="mr-5"
                />
                <v-btn
                    type="submit"
                    color="primary"
                    @click="save"
                >
                    <v-icon
                        left
                        small
                    >
                        fas fa-save
                    </v-icon>
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </ValidationObserver>
</template>

<script>
import { mapMutations, mapActions, mapGetters, mapState } from 'vuex';
import store from '../../../../store/store';
import SingleFont from '@/views/Admin/Config/FontPairing/SingleFont';
import { EventBus } from '@/store/eventBus';
import cacheGoogleFonts from '@/store/Common/GoogleFonts/cacheGoogleFonts';
import setWeight from '@/store/Modules/lib/setWeight';
import setStyle from '@/store/Modules/lib/setStyle';

export default {
    components: { SingleFont },
    computed: {
        name: {
            get: function() {
                return store.state.fontPairingEditor.name;
            },
            set: function(value) {
                this.setName(value);
            }
        },

        order: {
            get: function() {
                return store.state.fontPairingEditor.order;
            },
            set: function(value) {
                this.setOrder(value);
            }
        },

        published: {
            get: function() {
                return store.state.fontPairingEditor.published;
            },
            set: function(value) {
                this.setPublished(value);
            }
        },

        ...mapState(
            'fontPairingEditor',
            [
                'pairings',
                'fonts',
                'fontPairingId'
            ]
        ),

        ...mapGetters({
            publishedText: 'fontPairingEditor/publishedText',
            h1Style: 'fontPairingEditor/h1Style',
            h1SwatchStyle: 'fontPairingEditor/h1SwatchStyle',
            h2Style: 'fontPairingEditor/h2Style',
            subheadStyle: 'fontPairingEditor/subheadStyle',
            buttonStyle: 'fontPairingEditor/buttonStyle',
            bodyStyle: 'fontPairingEditor/bodyStyle',
            bodySwatchStyle: 'fontPairingEditor/bodySwatchStyle',
            bannerStyle: 'fontPairingEditor/bannerStyle'
        })
    },

    data() {
        return {
            fontsLoaded: false,
            webfonts: [],
            cacheFontButtonEnabled: false,
        };
    },

    mounted() {
        this.getFonts()
            .then(() => {
                this.fontsLoaded = true;
            });

        EventBus.$on('FontEditPreviewChanged', () => {
            this.loadWebFonts();
        });
    },

    beforeDestroy() {
        this.resetState();
    },

    methods: {
        ...mapActions({
            showNotification: 'notification/show',
            getData: 'fontPairingEditor/getData',
            saveData: 'fontPairingEditor/saveData',
            getFonts: 'fontPairingEditor/getFonts',
            resetState: 'fontPairingEditor/resetState',
        }),

        ...mapMutations({
            toggleOverlay: 'overlay/toggle',
            setId: 'fontPairingEditor/setFontPairingId',
            setName: 'fontPairingEditor/setName',
            setOrder: 'fontPairingEditor/setOrder',
            setPublished: 'fontPairingEditor/setPublished',
            toggleDeleteModal: 'fontPairingEditor/toggleDeleteModal',
            setFontPreviewStyle: 'fontPairingEditor/setFontPreviewStyle'
        }),

        save() {
            this.saveData();
        },

        updateFontCache() {
            cacheGoogleFonts();
        },

        loadWebFonts() {
            this.webfonts = Object.values(this.pairings);

            const googleFonts = [];

            this.webfonts.forEach(font => {
                googleFonts.push(font.family + ':' + font.variant);
            });

            window.WebFont.load({
                google: {
                    families: googleFonts
                }
            });

            this.generateCSS();
        },

        prepareCSSClass(font) {
            const family = font.family;
            const familyVariant = font.variant ? font.variant : false;
            const weight = setWeight(familyVariant);
            const italic = setStyle(familyVariant);

            if (!family) {
                return;
            }

            let style = '';

            style += 'font-family:' + family + ';';
            style += 'font-weight:' + weight + ';';
            style += 'font-style:' + italic + ';';
            style += 'font-size:' + font.preview_size + 'px;';
            style += 'line-height:' + font.preview_line_height + ';';
            style += 'letter-spacing:' + font.preview_letter_spacing + 'px;';

            if (font.all_caps) {
                style += 'text-transform:' + 'uppercase' + ';';
            }

            let storeData = {
                key: font.key,
                style: style
            };

            this.setFontPreviewStyle(storeData);

            if (font.key === 'h1' || font.key === 'body') {
                style = '';
                style += 'font-family:' + family + ';';
                style += 'font-weight:' + weight + ';';
                style += 'font-style:' + italic + ';';
                style += 'line-height:' + font.selector_line_height + ';';
                style += 'letter-spacing:' + font.selector_letter_spacing + 'px;';
                style += 'font-size:' + font.selector_size + 'px;';

                if (font.all_caps) {
                    style += 'text-transform:' + 'uppercase' + ';';
                }

                let storeData = {
                    key: font.key + 'Swatch',
                    style: style
                };

                this.setFontPreviewStyle(storeData);
            }
        },
        generateCSS() {
            this.webfonts.forEach(font => {
                this.prepareCSSClass(font);
            });
        }
    }
};

</script>

<style lang="scss">
.FontConfigurator {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    align-content: flex-start;
    align-items: flex-start;
    margin-bottom: 0 !important;
}

.FontConfigurator__preview {
    display: flex;
    flex-flow: row wrap;
    align-content: flex-start;
    align-items: flex-start;
    flex: 0 0 40%;
    width: 40%;
    padding: 24px;
    margin: 0 1px;
    border: 1px solid currentColor;
    border-radius: $border-radius-root;
    margin-bottom: 16px !important;
}

.FontConfigurator__inputs {
    flex: 0 0 58%;
    width: 58%;
    margin: 0 1px;
    border-radius: $border-radius-root;
    margin-bottom: 16px !important;
}

.FontConfigurator__preview__h1 {
    margin-bottom: 12px;
    line-height: 1.4;
}

.FontConfigurator__preview__h2 {
    margin-bottom: 12px;
    line-height: 1.4;
}

.FontConfigurator__preview__subhead {
    margin-bottom: 12px;
    line-height: 1.4;
}

.FontConfigurator__preview__banner {
    margin-bottom: 12px;
    line-height: 1.4;
}

.FontConfigurator__preview__body {
    margin-bottom: 12px;
    line-height: 1.4;
}

.FontConfigurator__preview__button {
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    align-content: center;
    align-items: center;
    flex: 0 0 100%;
    width: 100%;
    margin: 0 auto 36px;
    padding: 12px;
    border-radius: 6px;
    background-color: $primary-500;
    color: white;
    line-height: 1.1;
}

.FontConfigurator__preview__button span {
    margin-top: -2px;
}

.FontConfigurator__preview__swatch {
    flex: 0 0 275px;
    width: 275px;
    padding: 12px;
    border: 1px solid currentColor;
    margin: 0 auto;

    p {
        margin: 0;
    }
}

.FontConfigurator__preview__swatch__h1 {
    margin-bottom: 12px;
}

</style>
